import { useState, useEffect, useCallback, useMemo } from "react";
import { Grid, CardContent, Typography, Paper, Box, TextField, Alert, AlertTitle } from "@mui/material";
import { IUser, ICrewBase, IPosition, ISimpleUser } from "@vmosolutions/oc-interfaces";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { useCancellablePromise } from "src/API/useCancellablePromise";
import { IActiveModal } from "src/pages/Admin/AdminWindow";
import { useSelector, useDispatch } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { sortUsersByLastName } from "../Users/ManageUsers";
import { updateCrewMember } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { CrewForm } from "./CrewForm";
import LoadingWrapper from "src/components/LoadingWrapper/LoadingWrapper";
import HelpIcon from "@mui/icons-material/Help";
import { DraggableListItem } from "src/components/DraggableListItem/DraggableListItem";
import BadgeIcon from "@mui/icons-material/Badge";
import PersonIcon from "@mui/icons-material/Person";
import { PositionBadge } from "src/components/CrewSchedule/PositionBadge";
import { sortByDisplayOrder } from "src/utils";
interface IManageCrewProps {
  id: string;
  setActiveModal: (activeModal: IActiveModal | null) => void;
  height: number;
}

export const formatCrewList = (users: (IUser | ISimpleUser)[], positions: IPosition[]): any => {
  if (positions.length > 0 && users.length > 0) {
    const unassignedLabel = "Unassigned";
    const inactiveLabel = "Inactive";
    const crewList = users.filter((user): user is IUser | ISimpleUser => !!user?.crewInfo);
    const crewObj = crewList.reduce(
      (obj: { [key: string]: (IUser | ISimpleUser)[] }, crew) => {
        const heldPositions = crew.crewInfo.positions ?? [];
        const isActive = crew.crewInfo.isActive;
        // An active crew member is listed under every position they hold. Bucketing by the single
        // lowest-displayOrder position left the ENG-440 groups permanently showing "(0)", because a
        // trainer who also holds FA collapsed into the Flight Attendants group and never appeared
        // under Flight Attendant Trainer.
        const bucketTitles =
          heldPositions.length === 0
            ? [unassignedLabel]
            : isActive
              ? [...new Set(heldPositions.map((position) => position.title))]
              : [inactiveLabel];

        bucketTitles.forEach((title) => {
          if (!obj[title]) {
            obj[title] = [crew];
          } else {
            obj[title].push(crew);
          }
        });
        return obj;
      },
      {} as { [key: string]: (IUser | ISimpleUser)[] }
    );

    const crewGroups = [...positions]
      // Copy before sorting - Array.prototype.sort mutates, and this array comes from redux.
      .sort(sortByDisplayOrder)
      .reduce((acc, pos) => {
        if (!acc.find((group: any) => group.label === pos.description)) {
          const posList = crewObj[pos.title] ?? [];
          acc.push({
            label: `${pos.description}s (${posList.length})`,
            options: posList.sort(sortUsersByLastName),
          });
        }
        return acc;
      }, []);

    if (crewObj[unassignedLabel]?.length > 0) {
      crewGroups.unshift({
        label: unassignedLabel,
        options: crewObj?.[unassignedLabel] ? crewObj[unassignedLabel].sort(sortUsersByLastName) : [],
      });
    }

    if (crewObj[inactiveLabel]?.length > 0) {
      crewGroups.push({
        label: inactiveLabel,
        options: crewObj?.[inactiveLabel] ? crewObj[inactiveLabel].sort(sortUsersByLastName) : [],
      });
    }

    return crewGroups;
  } else {
    return [];
  }
};

export const ManageCrew = (props: IManageCrewProps): JSX.Element => {
  const { id, setActiveModal, height } = props;
  const dispatch = useDispatch();
  const { cancellablePromise } = useCancellablePromise();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingCrew, setIsLoadingCrew] = useState<boolean>(false);
  const [users, setUsers] = useState<IUser[]>([]);
  const [selectedCrew, setSelectedCrew] = useState<IUser>(null);
  const [groupedCrewList, setGroupedCrewList] = useState<any>([]);
  const [crewFilter, setCrewFilter] = useState<string>("");

  useEffect(() => {
    const getUsersAction = async (): Promise<void> => {
      const response = await AmplifyAPIWrapper.GetPaginate<IUser>("/users?simpleUser=true", null, cancellablePromise);
      setIsLoading(false);
      if (Array.isArray(response)) {
        setUsers(response);
      }
    };
    getUsersAction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [crewBases, setCrewBases] = useState<ICrewBase[]>([]);
  useEffect(() => {
    const getCrewBasesAction = async (): Promise<void> => {
      const response = await AmplifyAPIWrapper.Get<ICrewBase[]>("/structures/crewBases", null, cancellablePromise);
      if (Array.isArray(response)) {
        setCrewBases(response);
      }
    };
    getCrewBasesAction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Copy before sorting. Array.prototype.sort mutates, and this array is the redux-owned master
  // position list that every position selector in the client reads from, so sorting the selector
  // result directly reorders shared state as a side effect of rendering this screen.
  // Memoised because `positions` feeds dependency arrays below - a fresh array identity on every
  // render would re-run them continuously.
  const rawPositions: IPosition[] = useSelector((state: IRootState) => state.structures.customerStructure.positions);
  const positions: IPosition[] = useMemo(() => [...rawPositions].sort(sortByDisplayOrder), [rawPositions]);

  useEffect(() => {
    setGroupedCrewList(formatCrewList(users, positions));
  }, [positions, users]);

  const handleRetrieveCrew = useCallback((user: ISimpleUser): void => {
    if (user?._id) {
      setIsLoadingCrew(true);
      const getUserInfoAction = async (): Promise<void> => {
        const userInfo = await AmplifyAPIWrapper.Get<IUser>(`/user/${user._id}`, null, cancellablePromise);
        const userDetails = userInfo?._id ? userInfo : user;
        setSelectedCrew(userDetails as IUser);
        setIsLoadingCrew(false);
      };
      getUserInfoAction();
    } else {
      setSelectedCrew(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If users create, updated or delete user, update list.
  const updateCrewList = (crew: IUser, mode: "create" | "update" | "delete"): void => {
    const newUsers = [...users];
    const index = newUsers.findIndex((user: ISimpleUser) => user._id === crew._id);
    switch (true) {
      case crew._id && mode === "create": // Crew Member Created
        newUsers.push(crew);
        setSelectedCrew(crew);
        break;
      case crew._id && mode === "update" && index > -1: // Crew Member Updated
        newUsers[index] = crew;
        setSelectedCrew(crew);
        break;
      case crew._id && mode === "delete" && index > -1: // Crew Member Deleted
        newUsers.splice(index, 1);
        setSelectedCrew(null);
        break;
      default:
    }
    dispatch(updateCrewMember(crew, mode));
    setUsers(newUsers);
  };

  return (
    <CardContent>
      <Grid container item xs spacing={3}>
        <Grid container item xs={3} sx={{ width: "100%" }}>
          <Paper elevation={4} sx={{ pl: 1, pb: 1, width: "100%", height: `calc(${height}px - 40px)` }}>
            {isLoading ? (
              <Box sx={{ pt: 5 }}>
                <LoadingWrapper message="Loading Users" />
              </Box>
            ) : (
              <>
                <Grid item xs={12} sx={{ fontSize: "0.8em", alignItems: "center", display: "flex", p: 1 }}>
                  <HelpIcon fontSize="small" sx={{ mr: 1 }} />
                  To add a new crew member, search for the user below, and select them.
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    placeholder="Filter by name, email or employee ID"
                    variant="outlined"
                    size="small"
                    fullWidth
                    onChange={(e) => {
                      const val = e.target.value;
                      setCrewFilter(val);
                    }}
                  />
                </Grid>
                <Grid container item xs={12}>
                  <Grid item sx={{ width: "100%", overflowY: "scroll", height: `calc(${height}px - 140px)` }}>
                    {users
                      .filter((user) => {
                        const filter = crewFilter.toLowerCase().replace(/[^a-zA-Z0-9.\s]/g, "");
                        return (
                          user.firstName.toLowerCase().includes(filter) ||
                          user.lastName.toLowerCase().includes(filter) ||
                          user.email.toLowerCase().includes(filter) ||
                          (user.hasOwnProperty("employeeId") && user?.employeeId.includes(filter))
                        );
                      })
                      .sort((a, b) => {
                        if (a.active && !b.active) {
                          return -1;
                        } else if (!a.active && b.active) {
                          return 1;
                        } else {
                          if (a.lastName.toLowerCase() === b.lastName.toLowerCase()) {
                            return a.firstName.localeCompare(b.firstName);
                          } else {
                            return a.lastName.localeCompare(b.lastName);
                          }
                        }
                      })
                      .map((user: IUser, key: number) => {
                        return (
                          <DraggableListItem
                            active={user._id === selectedCrew?._id ? true : null}
                            key={key}
                            sx={{ cursor: "pointer", alignItems: "center", display: "flex" }}
                            onClick={() => {
                              handleRetrieveCrew(user);
                              setSelectedCrew(user);
                            }}
                          >
                            {user.hasOwnProperty("crewInfo") ? (
                              <BadgeIcon fontSize="small" />
                            ) : (
                              <PersonIcon fontSize="small" />
                            )}
                            <Box sx={{ pl: 1, width: "100%" }}>
                              {user.lastName}, {user.firstName}
                            </Box>
                            <Box sx={{ display: "flex" }}>
                              {user?.crewInfo &&
                                user?.crewInfo?.positions.map((position, key) => {
                                  return (
                                    <PositionBadge
                                      key={key}
                                      position={position}
                                      filled={true}
                                      isInactive={!user?.crewInfo?.isActive}
                                    />
                                  );
                                })}
                            </Box>
                            <Box>{user?.employeeId && user.employeeId}</Box>
                          </DraggableListItem>
                        );
                      })}
                  </Grid>
                </Grid>
              </>
            )}
          </Paper>
        </Grid>
        <Grid container item xs={9}>
          <Grid item xs={12} sx={{ display: "flex", alignItems: "center", minHeight: 50 }}>
            <Typography variant="h6">Manage Crew</Typography>
          </Grid>

          <Grid item xs={12} sx={{ height: `calc(${height}px - 100px)`, overflowY: "scroll" }}>
            {isLoadingCrew ? (
              <Box sx={{ pt: 5 }}>
                <LoadingWrapper message={`Loading ${selectedCrew.firstName} ${selectedCrew.lastName}`} />
              </Box>
            ) : (
              <>
                {selectedCrew ? (
                  <CrewForm
                    users={users}
                    selectedCrew={selectedCrew}
                    handleRetrieveCrew={handleRetrieveCrew}
                    updateCrewList={updateCrewList}
                    crewBases={crewBases}
                    positions={positions}
                    setActiveModal={setActiveModal}
                  />
                ) : (
                  <Alert severity="info" variant="filled">
                    <AlertTitle sx={{ color: "common.white" }}>Getting started</AlertTitle>
                    Choose a user from the list to the left to get started. All crew are users in the system, so to add
                    a new crew member, make sure they are a user first, in the users tab.
                  </Alert>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </Grid>
    </CardContent>
  );
};
